import React, { useState, useRef, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  X, 
  Upload, 
  Loader2, 
  AlertCircle, 
  User, 
  Sparkles,
  FileArchive,
  Image as ImageIcon,
  Trash2,
  Info,
  CheckCircle2
} from 'lucide-react'
import { useCharacterStore } from '../../stores/characterStore'
import { useAuthStore } from '../../stores/authStore'

interface CreateCharacterDialogProps {
  open: boolean
  onClose: () => void
  projectId: string
}

interface UploadedImage {
  file: File
  preview: string
  id: string
}

const CreateCharacterDialog: React.FC<CreateCharacterDialogProps> = ({
  open,
  onClose,
  projectId
}) => {
  const [name, setName] = useState('')
  const [triggerWord, setTriggerWord] = useState('')
  const [description, setDescription] = useState('')
  const [trainingSteps, setTrainingSteps] = useState(1000)
  const [isStyle, setIsStyle] = useState(false)
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([])
  const [zipFile, setZipFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isTraining, setIsTraining] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [trainingStatus, setTrainingStatus] = useState<'idle' | 'uploading' | 'training' | 'complete' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const fileInputRef = useRef<HTMLInputElement>(null)
  const zipInputRef = useRef<HTMLInputElement>(null)
  
  const { createCharacter, uploadTrainingImages, loading, error, trainingInProgress } = useCharacterStore()
  const { user } = useAuthStore()

  // Auto-generate trigger word from name
  useEffect(() => {
    if (name && !triggerWord) {
      const cleanName = name.toLowerCase().replace(/[^a-z0-9]/g, '')
      setTriggerWord(cleanName || 'character')
    }
  }, [name])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    handleFilesSelected(files)
  }, [])

  const handleFilesSelected = (files: File[]) => {
    // Check if it's a ZIP file
    const zipFiles = files.filter(f => f.type === 'application/zip' || f.name.endsWith('.zip'))
    if (zipFiles.length > 0) {
      setZipFile(zipFiles[0])
      setUploadedImages([]) // Clear individual images if ZIP is uploaded
      return
    }

    // Handle individual images
    const imageFiles = files.filter(f => f.type.startsWith('image/'))
    const newImages: UploadedImage[] = imageFiles.map((file, index) => ({
      file,
      preview: URL.createObjectURL(file),
      id: `${Date.now()}_${index}`
    }))

    setUploadedImages(prev => [...prev, ...newImages])
    setZipFile(null) // Clear ZIP if images are uploaded
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFilesSelected(Array.from(e.target.files))
    }
  }

  const handleZipInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setZipFile(e.target.files[0])
      setUploadedImages([]) // Clear individual images
    }
  }

  const handleRemoveImage = (id: string) => {
    setUploadedImages(prev => {
      const image = prev.find(img => img.id === id)
      if (image) {
        URL.revokeObjectURL(image.preview)
      }
      return prev.filter(img => img.id !== id)
    })
  }

  const handleRemoveZip = () => {
    setZipFile(null)
  }

  const handleSubmit = async () => {
    if (!name.trim() || !triggerWord.trim()) {
      setErrorMessage('Please provide a name and trigger word')
      return
    }

    if (uploadedImages.length === 0 && !zipFile) {
      setErrorMessage('Please upload at least one training image or a ZIP file')
      return
    }

    if (!user) {
      setErrorMessage('You must be logged in to create a character')
      return
    }

    setErrorMessage('')
    setTrainingStatus('uploading')
    setIsUploading(true)

    try {
      let imagesZipUrl: string

      if (zipFile) {
        // Upload the ZIP file directly to fal.storage for better compatibility
        const fal = await import('@fal-ai/serverless-client')
        imagesZipUrl = await fal.storage.upload(zipFile)
        console.log('ZIP uploaded to fal.storage:', imagesZipUrl)
      } else {
        // Upload individual images - create ZIP and upload
        imagesZipUrl = await uploadTrainingImages(
          uploadedImages.map(img => img.file),
          projectId
        )
      }

      setUploadProgress(100)
      setTrainingStatus('training')
      setIsUploading(false)
      setIsTraining(true)

      // Create and train character
      await createCharacter({
        projectId,
        ownerId: user.uid,
        name: name.trim(),
        triggerWord: triggerWord.trim(),
        description: description.trim(),
        trainingSteps,
        isStyle,
        imagesZipUrl
      })

      setTrainingStatus('complete')
      
      // Close dialog after a short delay to show success
      setTimeout(() => {
        handleClose()
      }, 2000)

    } catch (err) {
      console.error('Failed to create character:', err)
      setErrorMessage(err instanceof Error ? err.message : 'Failed to create character')
      setTrainingStatus('error')
    } finally {
      setIsUploading(false)
      setIsTraining(false)
    }
  }

  const handleClose = () => {
    if (!isUploading && !isTraining) {
      // Cleanup previews
      uploadedImages.forEach(img => URL.revokeObjectURL(img.preview))
      setUploadedImages([])
      setZipFile(null)
      setName('')
      setTriggerWord('')
      setDescription('')
      setTrainingSteps(1000)
      setIsStyle(false)
      setTrainingStatus('idle')
      setErrorMessage('')
      onClose()
    }
  }

  // Esc key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open && !isUploading && !isTraining) {
        handleClose()
      }
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [open, isUploading, isTraining])

  if (!open) return null

  const hasApiKey = !!import.meta.env.VITE_FAL_API_KEY && import.meta.env.VITE_FAL_API_KEY !== 'your_fal_api_key_here'
  const totalImages = zipFile ? '(ZIP)' : uploadedImages.length

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[60] p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="glass-card w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-white/20"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-headline text-white">Create Character</h2>
                <p className="text-xs text-white/50">Train a custom AI character with your images</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isUploading || isTraining}
              className="glass-button p-2 hover:bg-white/20 text-white/70 hover:text-white disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {!hasApiKey ? (
              <div className="glass-card bg-yellow-500/20 border border-yellow-500/30 p-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <div className="text-yellow-200">
                    <h3 className="font-semibold mb-2">API Key Required</h3>
                    <p className="text-sm">
                      To train custom characters, you need a fal.ai API key. Add it to your environment variables.
                    </p>
                  </div>
                </div>
              </div>
            ) : trainingStatus === 'complete' ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-400" />
                </div>
                <h3 className="text-2xl font-headline text-white mb-2">Character Created!</h3>
                <p className="text-white/70">
                  Your character "{name}" is now ready to use for image generation.
                </p>
              </div>
            ) : trainingStatus === 'training' ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                  <Sparkles className="w-10 h-10 text-purple-400" />
                </div>
                <h3 className="text-2xl font-headline text-white mb-2">Training in Progress</h3>
                <p className="text-white/70 mb-6">
                  This may take 5-15 minutes depending on your settings.
                </p>
                <div className="w-full max-w-xs mx-auto bg-white/10 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse" style={{ width: '60%' }} />
                </div>
                <p className="text-sm text-white/50 mt-4">Training with {trainingSteps} steps...</p>
              </div>
            ) : (
              <>
                {/* Character Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-white font-medium mb-2">
                      Character Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g., Sarah, Robot Helper, Fantasy Elf"
                      className="w-full glass-card bg-white/5 border border-white/20 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 placeholder:text-white/40"
                      disabled={isUploading || isTraining}
                    />
                  </div>

                  <div>
                    <label className="block text-white font-medium mb-2">
                      Trigger Word <span className="text-red-400">*</span>
                      <span className="text-white/50 text-sm font-normal ml-2">
                        (use this word in prompts to activate the character)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={triggerWord}
                      onChange={(e) => setTriggerWord(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                      placeholder="e.g., sarah, robothelper"
                      className="w-full glass-card bg-white/5 border border-white/20 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 placeholder:text-white/40 font-mono"
                      disabled={isUploading || isTraining}
                    />
                    <p className="text-xs text-white/40 mt-1">
                      Only lowercase letters and numbers. Example prompt: "A photo of {triggerWord || 'character'} in a garden"
                    </p>
                  </div>

                  <div>
                    <label className="block text-white font-medium mb-2">
                      Description <span className="text-white/50 text-sm font-normal">(optional)</span>
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Brief description of this character..."
                      className="w-full glass-card bg-white/5 border border-white/20 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 placeholder:text-white/40 resize-none"
                      rows={2}
                      disabled={isUploading || isTraining}
                    />
                  </div>
                </div>

                {/* Training Settings */}
                <div className="glass-card bg-white/5 p-4 rounded-lg space-y-4">
                  <h3 className="text-white font-medium flex items-center space-x-2">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    <span>Training Settings</span>
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Training Steps</label>
                      <div className="flex gap-2">
                        <select
                          value={[500, 1000, 1500, 2000].includes(trainingSteps) ? trainingSteps : 'custom'}
                          onChange={(e) => {
                            const val = e.target.value
                            if (val === 'custom') {
                              setTrainingSteps(3000)
                            } else {
                              setTrainingSteps(Number(val))
                            }
                          }}
                          className="flex-1 glass-card bg-white/5 border border-white/20 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                          disabled={isUploading || isTraining}
                        >
                          <option value={500} className="bg-gray-800">500 (Fast)</option>
                          <option value={1000} className="bg-gray-800">1000 (Recommended)</option>
                          <option value={1500} className="bg-gray-800">1500 (Better)</option>
                          <option value={2000} className="bg-gray-800">2000 (Best)</option>
                          <option value="custom" className="bg-gray-800">Custom</option>
                        </select>
                        {![500, 1000, 1500, 2000].includes(trainingSteps) && (
                          <input
                            type="number"
                            value={trainingSteps}
                            onChange={(e) => setTrainingSteps(Math.max(100, Math.min(10000, Number(e.target.value))))}
                            min={100}
                            max={10000}
                            step={100}
                            className="w-24 glass-card bg-white/5 border border-white/20 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-center"
                            disabled={isUploading || isTraining}
                          />
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-white/70 text-sm mb-2">Training Type</label>
                      <div className="flex bg-white/5 rounded-lg p-0.5">
                        <button
                          onClick={() => setIsStyle(false)}
                          className={`flex-1 py-2 text-sm rounded-md transition-colors ${!isStyle ? 'bg-purple-600 text-white' : 'text-white/50'}`}
                          disabled={isUploading || isTraining}
                        >
                          Character
                        </button>
                        <button
                          onClick={() => setIsStyle(true)}
                          className={`flex-1 py-2 text-sm rounded-md transition-colors ${isStyle ? 'bg-purple-600 text-white' : 'text-white/50'}`}
                          disabled={isUploading || isTraining}
                        >
                          Style
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-2 text-xs text-white/50 bg-blue-500/10 p-3 rounded-lg">
                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-400" />
                    <p>
                      <strong className="text-white/70">Character</strong> trains on a specific person/character. 
                      <strong className="text-white/70 ml-1">Style</strong> trains on artistic styles. 
                      Training costs ~$2 per run and takes 5-15 minutes.
                    </p>
                  </div>
                </div>

                {/* Image Upload */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-white font-medium">
                      Training Images <span className="text-red-400">*</span>
                    </label>
                    <span className="text-white/50 text-sm">
                      {totalImages} images
                    </span>
                  </div>

                  {/* Hidden file inputs */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  <input
                    ref={zipInputRef}
                    type="file"
                    accept=".zip,application/zip"
                    onChange={handleZipInputChange}
                    className="hidden"
                  />

                  {/* Drop zone */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                      isDragging 
                        ? 'border-purple-400 bg-purple-500/10' 
                        : 'border-white/20 hover:border-white/40 hover:bg-white/5'
                    } ${(isUploading || isTraining) ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    <div className="flex flex-col items-center space-y-4">
                      <div className="flex space-x-4">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="glass-button px-4 py-2 flex items-center space-x-2 bg-white/10 hover:bg-white/20"
                          disabled={isUploading || isTraining}
                        >
                          <ImageIcon className="w-4 h-4" />
                          <span>Upload Images</span>
                        </button>
                        <button
                          onClick={() => zipInputRef.current?.click()}
                          className="glass-button px-4 py-2 flex items-center space-x-2 bg-white/10 hover:bg-white/20"
                          disabled={isUploading || isTraining}
                        >
                          <FileArchive className="w-4 h-4" />
                          <span>Upload ZIP</span>
                        </button>
                      </div>
                      <p className="text-white/50 text-sm">
                        or drag and drop images here
                      </p>
                      <p className="text-white/40 text-xs">
                        Recommended: 10-20 high-quality images (1024x1024 or larger)
                      </p>
                    </div>
                  </div>

                  {/* ZIP file preview */}
                  {zipFile && (
                    <div className="glass-card bg-white/5 p-4 rounded-lg flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <FileArchive className="w-8 h-8 text-purple-400" />
                        <div>
                          <p className="text-white font-medium">{zipFile.name}</p>
                          <p className="text-white/50 text-sm">{(zipFile.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                      </div>
                      <button
                        onClick={handleRemoveZip}
                        className="p-2 hover:bg-white/10 rounded-lg text-white/50 hover:text-red-400 transition-colors"
                        disabled={isUploading || isTraining}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  )}

                  {/* Image previews */}
                  {uploadedImages.length > 0 && (
                    <div className="grid grid-cols-5 gap-2">
                      {uploadedImages.map((img) => (
                        <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden group">
                          <img
                            src={img.preview}
                            alt="Training image"
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => handleRemoveImage(img.id)}
                            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/90 hover:bg-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            disabled={isUploading || isTraining}
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Error message */}
                {(errorMessage || error) && (
                  <div className="glass-card bg-red-500/20 border border-red-500/30 p-4 rounded-lg flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-red-200 text-sm">{errorMessage || error}</p>
                  </div>
                )}

                {/* Submit button */}
                <button
                  onClick={handleSubmit}
                  disabled={!name.trim() || !triggerWord.trim() || (uploadedImages.length === 0 && !zipFile) || isUploading || isTraining}
                  className="w-full glass-button bg-gradient-to-r from-purple-500/30 to-pink-500/30 hover:from-purple-500/40 hover:to-pink-500/40 text-white font-semibold px-6 py-4 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Uploading Images...</span>
                    </>
                  ) : isTraining ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Training Character...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      <span>Start Training (~$2)</span>
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default CreateCharacterDialog

